import React, { useState, useEffect } from 'react';
import config from '../../config';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  useTheme,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useLanguage } from '../../contexts/LanguageContext';
import { PurchaseOrder } from '../../types/purchaseOrders.types';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';

interface PurchaseOrderPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  purchaseOrder: PurchaseOrder | null;
  onEdit?: () => void;
}

const PurchaseOrderPreviewDialog: React.FC<PurchaseOrderPreviewDialogProps> = ({
  open,
  onClose,
  purchaseOrder,
  onEdit
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    if (open) {
      loadCompanyInfo();
    }
  }, [open, purchaseOrder]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  if (!purchaseOrder) return null;

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'Draft': return isRTL ? 'مسودة' : 'Draft';
      case 'Approved': return isRTL ? 'معتمد' : 'Approved';
      case 'Received': return isRTL ? 'مستلم' : 'Received';
      case 'Cancelled': return isRTL ? 'ملغي' : 'Cancelled';
      default: return status || '-';
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Draft': return '#9e9e9e';
      case 'Approved': return '#2196f3';
      case 'Received': return '#4caf50';
      case 'Cancelled': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const getStatusChipColor = (status?: string) => {
    switch (status) {
      case 'Draft': return 'default';
      case 'Approved': return 'info';
      case 'Received': return 'success';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.000';
    return amount.toFixed(3);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) return;

    const dir = isRTL ? 'rtl' : 'ltr';
    const align = isRTL ? 'right' : 'left';
    const alignOpp = isRTL ? 'left' : 'right';
    const currency = isRTL ? 'د.ك' : 'KWD';

    let itemsRows = '';
    purchaseOrder.details.forEach((detail, idx) => {
      const lineTotal = detail.orderedQuantity * detail.unitPrice;
      itemsRows += `
        <tr>
          <td style="text-align:center;">${idx + 1}</td>
          <td><strong>${detail.itemCode}</strong><br/><span style="color:#666;font-size:11px;">${isRTL ? detail.itemNameAr : detail.itemName}</span></td>
          <td style="text-align:center;">${isRTL ? detail.unitNameAr : detail.unitName}</td>
          <td style="text-align:center;font-weight:bold;">${formatCurrency(detail.orderedQuantity)}</td>
          <td style="text-align:center;font-weight:bold;">${formatCurrency(detail.unitPrice)}</td>
          <td style="text-align:center;font-weight:bold;color:#1565c0;">${formatCurrency(lineTotal)}</td>
        </tr>
      `;
    });

    // Totals rows
    let totalsRows = `
      <tr style="background:#e3f2fd;">
        <td colspan="5" style="text-align:${alignOpp};font-weight:bold;padding:8px;">${isRTL ? 'المجموع الفرعي:' : 'Subtotal:'}</td>
        <td style="text-align:center;font-weight:bold;padding:8px;">${formatCurrency(purchaseOrder.subTotal)} ${currency}</td>
      </tr>
    `;

    if ((purchaseOrder.discountAmount ?? 0) > 0) {
      totalsRows += `
        <tr>
          <td colspan="5" style="text-align:${alignOpp};font-weight:bold;padding:6px;color:#f44336;">${isRTL ? `الخصم (${purchaseOrder.discountPercent}%):` : `Discount (${purchaseOrder.discountPercent}%):`}</td>
          <td style="text-align:center;font-weight:bold;padding:6px;color:#f44336;">-${formatCurrency(purchaseOrder.discountAmount)} ${currency}</td>
        </tr>
      `;
    }

    if ((purchaseOrder.taxAmount ?? 0) > 0) {
      totalsRows += `
        <tr>
          <td colspan="5" style="text-align:${alignOpp};font-weight:bold;padding:6px;color:#ff9800;">${isRTL ? `الضريبة (${purchaseOrder.taxPercent}%):` : `Tax (${purchaseOrder.taxPercent}%):`}</td>
          <td style="text-align:center;font-weight:bold;padding:6px;color:#ff9800;">+${formatCurrency(purchaseOrder.taxAmount)} ${currency}</td>
        </tr>
      `;
    }

    if ((purchaseOrder.shippingCost ?? 0) > 0) {
      totalsRows += `
        <tr>
          <td colspan="5" style="text-align:${alignOpp};font-weight:bold;padding:6px;color:#2196f3;">${isRTL ? 'تكلفة الشحن:' : 'Shipping Cost:'}</td>
          <td style="text-align:center;font-weight:bold;padding:6px;color:#2196f3;">+${formatCurrency(purchaseOrder.shippingCost)} ${currency}</td>
        </tr>
      `;
    }

    totalsRows += `
      <tr style="background:#1565c0;color:white;">
        <td colspan="5" style="text-align:${alignOpp};font-weight:bold;padding:10px;font-size:14px;">${isRTL ? 'الإجمالي الكلي:' : 'Total Amount:'}</td>
        <td style="text-align:center;font-weight:bold;padding:10px;font-size:16px;">${formatCurrency(purchaseOrder.totalAmount)} ${currency}</td>
      </tr>
    `;

    printWindow.document.write(`
      <html dir="${dir}">
      <head>
        <title>${isRTL ? 'طلب شراء' : 'Purchase Order'} - ${purchaseOrder.poNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; direction: ${dir}; color: #333; font-size: 13px; }
          .header { text-align: center; border-bottom: 3px solid #2196f3; padding-bottom: 15px; margin-bottom: 15px; }
          .header img { max-height: 60px; margin-bottom: 8px; }
          .header h2 { color: #2196f3; margin: 5px 0; font-size: 20px; }
          .header .subtitle { font-size: 11px; color: #666; }
          .doc-title { text-align: center; margin-bottom: 20px; }
          .doc-title h3 { color: #2196f3; font-size: 24px; border: 2px solid #2196f3; display: inline-block; padding: 8px 30px; border-radius: 8px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; border: 1px solid #ddd; border-radius: 8px; padding: 15px; }
          .info-item label { font-size: 11px; color: #888; display: block; margin-bottom: 2px; }
          .info-item .value { font-size: 14px; font-weight: bold; }
          .info-item .value.primary { color: #1565c0; font-size: 16px; }
          .status-badge { display: inline-block; padding: 3px 12px; border-radius: 12px; font-size: 12px; font-weight: bold; color: white; }
          .supplier-box { background: #e3f2fd; border: 1px solid #90caf9; border-radius: 8px; padding: 12px; margin-bottom: 10px; }
          .supplier-box label { font-size: 11px; color: #666; }
          .supplier-box .name { font-size: 16px; font-weight: bold; color: #1565c0; }
          .supplier-box .code { font-size: 11px; color: #888; }
          .warehouse-box { background: #e8f5e9; border: 1px solid #a5d6a7; border-radius: 8px; padding: 12px; margin-bottom: 10px; }
          .warehouse-box label { font-size: 11px; color: #666; }
          .warehouse-box .name { font-size: 14px; font-weight: bold; color: #2e7d32; }
          .warehouse-box .code { font-size: 11px; color: #888; }
          .notes-box { background: #fff3e0; border: 1px solid #ffe0b2; border-radius: 8px; padding: 10px; margin-bottom: 15px; }
          .notes-box label { font-size: 11px; color: #888; font-weight: bold; }
          .section-title { background: #2196f3; color: white; padding: 10px 15px; font-weight: bold; font-size: 14px; border-radius: 8px 8px 0 0; }
          table { width: 100%; border-collapse: collapse; }
          thead th { background: #e3f2fd; color: #1565c0; padding: 10px 8px; font-size: 12px; text-align: ${align}; border-bottom: 2px solid #90caf9; }
          thead th.center { text-align: center; }
          tbody td { padding: 8px; border-bottom: 1px solid #eee; font-size: 12px; }
          tbody tr:nth-child(even) { background: #fafafa; }
          .table-wrapper { border: 1px solid #ddd; border-radius: 0 0 8px 8px; overflow: hidden; margin-bottom: 20px; }
          .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
          .sig-block { text-align: center; width: 30%; }
          .sig-line { border-top: 1px solid #333; padding-top: 8px; margin-top: 60px; font-weight: bold; font-size: 12px; }
          .print-date { text-align: ${alignOpp}; font-size: 10px; color: #999; margin-bottom: 10px; }
          @media print { @page { size: A4; margin: 10mm; } }
        </style>
      </head>
      <body>
        <div class="print-date">${isRTL ? 'تاريخ الطباعة:' : 'Print Date:'} ${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>

        ${companyInfo ? `
        <div class="header">
          ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}
          <h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
          <div class="subtitle">
            ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
          </div>
        </div>
        ` : ''}

        <div class="doc-title">
          <h3>${isRTL ? 'طلب شراء' : 'Purchase Order'}</h3>
        </div>

        <div class="info-grid">
          <div class="info-item">
            <label>${isRTL ? 'رقم طلب الشراء' : 'PO Number'}</label>
            <div class="value primary">${purchaseOrder.poNumber}</div>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'التاريخ' : 'Date'}</label>
            <div class="value">${formatDate(purchaseOrder.poDate)}</div>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'الحالة' : 'Status'}</label>
            <div><span class="status-badge" style="background:${getStatusColor(purchaseOrder.status)}">${getStatusLabel(purchaseOrder.status)}</span></div>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'التاريخ المتوقع' : 'Expected Date'}</label>
            <div class="value">${purchaseOrder.expectedDeliveryDate ? formatDate(purchaseOrder.expectedDeliveryDate) : '-'}</div>
          </div>
        </div>

        <div class="supplier-box">
          <label>${isRTL ? 'المورد' : 'Supplier'}</label>
          <div class="name">${isRTL ? purchaseOrder.supplierNameAr : purchaseOrder.supplierName}</div>
          <div class="code">${purchaseOrder.supplierCode}</div>
        </div>

        <div class="warehouse-box">
          <label>${isRTL ? 'المخزن' : 'Warehouse'}</label>
          <div class="name">${isRTL ? purchaseOrder.warehouseNameAr : purchaseOrder.warehouseName}</div>
          <div class="code">${purchaseOrder.warehouseCode}</div>
        </div>

        ${purchaseOrder.notes ? `
        <div class="notes-box">
          <label>${isRTL ? 'ملاحظات:' : 'Notes:'}</label>
          <div>${purchaseOrder.notes}</div>
        </div>
        ` : ''}

        <div class="section-title">📦 ${isRTL ? 'تفاصيل الأصناف' : 'Items Details'}</div>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th class="center">#</th>
                <th>${isRTL ? 'الصنف' : 'Item'}</th>
                <th class="center">${isRTL ? 'الوحدة' : 'Unit'}</th>
                <th class="center">${isRTL ? 'الكمية' : 'Quantity'}</th>
                <th class="center">${isRTL ? 'السعر' : 'Price'}</th>
                <th class="center">${isRTL ? 'الإجمالي' : 'Total'}</th>
              </tr>
            </thead>
            <tbody>
              ${itemsRows}
              ${totalsRows}
            </tbody>
          </table>
        </div>

        <div class="signatures">
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المعتمد' : 'Approved By'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'مدير المشتريات' : 'Purchasing Manager'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'توقيع المورد' : 'Supplier Signature'}</div></div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      // printWindow stays open for user review
    }, 500);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: isDarkMode ? '#1a1a2e' : 'white'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: isDarkMode
            ? 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)'
            : 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCartIcon />
          <Box component="span">
            {isRTL ? 'معاينة طلب شراء' : 'Purchase Order Preview'}
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {/* Header Info */}
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 3,
            bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'رقم طلب الشراء' : 'PO Number'}
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {purchaseOrder.poNumber}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'التاريخ' : 'Date'}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {formatDate(purchaseOrder.poDate)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'الحالة' : 'Status'}
              </Typography>
              <Box>
                <Chip
                  label={getStatusLabel(purchaseOrder.status)}
                  color={getStatusChipColor(purchaseOrder.status)}
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'التاريخ المتوقع' : 'Expected Date'}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {purchaseOrder.expectedDeliveryDate ? formatDate(purchaseOrder.expectedDeliveryDate) : '-'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Supplier Info */}
          <Box sx={{ p: 2, bgcolor: isDarkMode ? 'rgba(33,150,243,0.1)' : '#e3f2fd', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
              {isRTL ? 'المورد' : 'Supplier'}
            </Typography>
            <Typography variant="h6" fontWeight="bold" color="primary">
              {isRTL ? purchaseOrder.supplierNameAr : purchaseOrder.supplierName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {purchaseOrder.supplierCode}
            </Typography>
          </Box>

          {/* Warehouse Info */}
          <Box sx={{ mt: 2, p: 2, bgcolor: isDarkMode ? 'rgba(76,175,80,0.1)' : '#e8f5e9', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
              {isRTL ? 'المخزن' : 'Warehouse'}
            </Typography>
            <Typography variant="body1" fontWeight="bold" color="success.main">
              {isRTL ? purchaseOrder.warehouseNameAr : purchaseOrder.warehouseName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {purchaseOrder.warehouseCode}
            </Typography>
          </Box>

          {purchaseOrder.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                {isRTL ? 'ملاحظات:' : 'Notes:'}
              </Typography>
              <Typography variant="body2">
                {purchaseOrder.notes}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Items Details */}
        <Paper
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              bgcolor: isDarkMode ? 'rgba(33,150,243,0.3)' : '#2196f3',
              color: 'white',
              p: 1.5,
              fontWeight: 'bold'
            }}
          >
            {isRTL ? '📦 تفاصيل الأصناف' : '📦 Items Details'}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الكمية' : 'Quantity'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'السعر' : 'Price'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الإجمالي' : 'Total'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrder.details.map((detail, idx) => {
                  const lineTotal = detail.orderedQuantity * detail.unitPrice;
                  return (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        <strong>{detail.itemCode}</strong>
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          {isRTL ? detail.itemNameAr : detail.itemName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{isRTL ? detail.unitNameAr : detail.unitName}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{formatCurrency(detail.orderedQuantity)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{formatCurrency(detail.unitPrice)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'primary.main' }}>{formatCurrency(lineTotal)}</TableCell>
                    </TableRow>
                  );
                })}
                
                {/* Subtotal */}
                <TableRow>
                  <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                    {isRTL ? 'المجموع الفرعي:' : 'Subtotal:'}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                    {formatCurrency(purchaseOrder.subTotal)} {isRTL ? 'د.ك' : 'KWD'}
                  </TableCell>
                </TableRow>

                {(purchaseOrder.discountAmount ?? 0) > 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                      {isRTL ? `الخصم (${purchaseOrder.discountPercent}%):` : `Discount (${purchaseOrder.discountPercent}%):`}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: 'error.main' }}>
                      -{formatCurrency(purchaseOrder.discountAmount)} {isRTL ? 'د.ك' : 'KWD'}
                    </TableCell>
                  </TableRow>
                )}

                {(purchaseOrder.taxAmount ?? 0) > 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                      {isRTL ? `الضريبة (${purchaseOrder.taxPercent}%):` : `Tax (${purchaseOrder.taxPercent}%):`}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: 'warning.main' }}>
                      +{formatCurrency(purchaseOrder.taxAmount)} {isRTL ? 'د.ك' : 'KWD'}
                    </TableCell>
                  </TableRow>
                )}

                {(purchaseOrder.shippingCost ?? 0) > 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                      {isRTL ? 'تكلفة الشحن:' : 'Shipping Cost:'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: 'info.main' }}>
                      +{formatCurrency(purchaseOrder.shippingCost)} {isRTL ? 'د.ك' : 'KWD'}
                    </TableCell>
                  </TableRow>
                )}

                <TableRow>
                  <TableCell colSpan={5} align="right" sx={{ 
                    fontWeight: 'bold', fontSize: '1rem',
                    bgcolor: isDarkMode ? 'rgba(33,150,243,0.2)' : 'rgba(33,150,243,0.1)'
                  }}>
                    {isRTL ? 'الإجمالي الكلي:' : 'Total Amount:'}
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    fontWeight: 'bold', fontSize: '1.1rem', color: 'success.main',
                    bgcolor: isDarkMode ? 'rgba(33,150,243,0.2)' : 'rgba(33,150,243,0.1)'
                  }}>
                    {formatCurrency(purchaseOrder.totalAmount)} {isRTL ? 'د.ك' : 'KWD'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
        <Button onClick={onClose} size="large">
          {isRTL ? 'إغلاق' : 'Close'}
        </Button>
        
        {purchaseOrder?.status === 'Draft' && onEdit && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => { onEdit(); onClose(); }}
            size="large"
            color="primary"
          >
            {isRTL ? 'تعديل' : 'Edit'}
          </Button>
        )}

        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          size="large"
          sx={{
            background: isDarkMode
              ? 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)'
              : 'linear-gradient(45deg, #2196f3 30%, #42a5f5 90%)',
          }}
        >
          {isRTL ? 'طباعة' : 'Print'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseOrderPreviewDialog;